import axios from 'axios';
import { loadingManager } from './loadingManager';

const axiosInstance = axios.create({
    baseURL: 'https://ig-c6fea48b852b4b04b0b3d4e4c2f38f5f.ecs.us-east-1.on.aws/',
    // baseURL: 'http://localhost:3001',
    headers: {
        'Content-Type': 'application/json',
    }
});

axiosInstance.interceptors.request.use(
    async (config) => {
        try {
            // Start loading (skip for background checks e.g. email/username exists)
            if (!config.skipLoader) {
                loadingManager.start();
            }
            
            // Get access token from localStorage
            const accessToken = localStorage.getItem('accessToken');
            if (accessToken) {
                config.headers.Authorization = `Bearer ${accessToken}`;
            }
            return config;
        } catch (error) {
            if (!config.skipLoader) loadingManager.stop();
            return Promise.reject({
                success: false,
                error: 'Request configuration error'
            });
        }
    }
);

// Helper function to logout user
const logoutUser = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('authUser');
    localStorage.removeItem('isAuthenticated');
    window.location.href = '/login';
};

// Helper function to refresh token (using plain axios to avoid interceptors)
const refreshAccessToken = async (refreshToken) => {
    try {
        // Use plain axios to avoid interceptors
        const response = await axios.post(
            `${axiosInstance.defaults.baseURL}/auth/refresh-token`,
            { refreshToken },
            {
                headers: {
                    'Content-Type': 'application/json',
                }
            }
        );
        return response;
    } catch (error) {
        throw error;
    }
};

axiosInstance.interceptors.response.use(
    (response) => {
        try {
            // Stop loading on successful response (skip if this request skipped the loader)
            if (!response.config.skipLoader) {
                loadingManager.stop();
            }
            return { success: true, data: response.data };
        } catch (error) {
            loadingManager.stop();
            return Promise.reject({
                success: false,
                error: 'Response parsing error'
            });
        }
    },
    async (error) => {
        const originalRequest = error.config;

        // Stop loading on error (skip if this request skipped the loader)
        if (!originalRequest?.skipLoader) {
            loadingManager.stop();
        }

        // If error is 401 and we haven't tried to refresh yet
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    // Try to refresh the token
                    const refreshResponse = await refreshAccessToken(refreshToken);
                    
                    // Check if refresh token API returned 200
                    if (refreshResponse.status === 200 && refreshResponse.data?.success && refreshResponse.data?.data) {
                        // Update tokens in localStorage
                        const { accessToken, refreshToken: newRefreshToken } = refreshResponse.data.data;
                        localStorage.setItem('accessToken', accessToken);
                        localStorage.setItem('refreshToken', newRefreshToken);
                        
                        // Update the original request with new access token
                        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                        
                        // Retry the original request
                        return axiosInstance(originalRequest);
                    } else {
                        // Refresh token API didn't return 200 or expected format, logout
                        logoutUser();
                        return Promise.reject({
                            success: false,
                            error: 'Token refresh failed'
                        });
                    }
                } else {
                    // No refresh token available, logout
                    logoutUser();
                    return Promise.reject({
                        success: false,
                        error: 'No refresh token available'
                    });
                }
            } catch (refreshError) {
                // If refresh fails (any status code other than 200), logout
                logoutUser();
                return Promise.reject({
                    success: false,
                    error: refreshError.response?.data?.message || 'Token refresh failed'
                });
            }
        }

        return Promise.reject({
            success: false,
            error: error.response?.data?.message || 'An error occurred'
        });
    }
);

// Authentication
const loginUser = async (data) => {
    const payload = {
        ...data,
        email: (data.email || '').trim().toLowerCase(),
    };
    return axiosInstance.post('/auth/login', payload);
}

const forgetPassword = async (data) => {
    const payload = { ...data };
    if (payload.email != null) {
        payload.email = String(payload.email).trim().toLowerCase();
    }
    return axiosInstance.post('/auth/forget-password', payload);
}

const updatePassword = async (data) => {
    // Use the existing axiosInstance - interceptor only adds Authorization header if token exists
    return axiosInstance.post('/auth/update-password', data);
}

const updatePasswordWithUserId = async (userId, data) => {
    // Use the existing axiosInstance - interceptor only adds Authorization header if token exists
    return axiosInstance.post(`/auth/update-password-userId/${userId}`, data);
}

// Email OTP
const sendEmailOtp = async (data) => {
    const payload = { ...data };
    if (payload.email != null) {
        payload.email = String(payload.email).trim().toLowerCase();
    }
    return axiosInstance.post('/email-otp/send', payload);
}

const verifyEmailOtp = async (data) => {
    const payload = { ...data };
    if (payload.email != null) {
        payload.email = String(payload.email).trim().toLowerCase();
    }
    return axiosInstance.post('/email-otp/verify', payload);
}

// Phone OTP (Twilio Verify) — `to` must be E.164, e.g. "+916383522927"
const normalizePhoneTo = (to) => String(to || '').replace(/[\s()-]/g, '').trim();

const sendPhoneOtp = async (data) => {
    const payload = { ...data };
    if (payload.to != null) {
        payload.to = normalizePhoneTo(payload.to);
    }
    return axiosInstance.post('/phone-otp/send', payload);
}

const verifyPhoneOtp = async (data) => {
    const payload = { ...data };
    if (payload.to != null) {
        payload.to = normalizePhoneTo(payload.to);
    }
    if (payload.code != null) {
        payload.code = String(payload.code).trim();
    }
    return axiosInstance.post('/phone-otp/verify', payload);
}

// User Management
const createUser = async (data) => {
    const dataToSend = { ...data };
    if (dataToSend.username != null) {
        dataToSend.username = String(dataToSend.username).trim().toLowerCase();
    }
    if (dataToSend.email != null) {
        dataToSend.email = String(dataToSend.email).trim().toLowerCase();
    }
    return axiosInstance.post('/user', dataToSend);
}

const createAccountContact = async (data) => {
    const payload = { ...data };
    if (payload.contact) {
        const contact = { ...payload.contact };
        if (contact.username != null) contact.username = String(contact.username).trim().toLowerCase();
        if (contact.email != null) contact.email = String(contact.email).trim().toLowerCase();
        payload.contact = contact;
    }
    if (payload.contact_2) {
        const contact2 = { ...payload.contact_2 };
        if (contact2.username != null) contact2.username = String(contact2.username).trim().toLowerCase();
        if (contact2.email != null) contact2.email = String(contact2.email).trim().toLowerCase();
        payload.contact_2 = contact2;
    }
    return axiosInstance.post('/account-contact', payload);
};

/** GET account-contact by contact id. Used for corporate_sponsor, ecosystem_partner, investor to load account + contact profile. */
const getAccountContact = async (contactId) => {
    if (!contactId) return { success: false, error: 'Contact ID is required' };
    return axiosInstance.get(`/account-contact?contactId=${contactId}`);
};

/** GET account-contact list by role. Returns { success, data: [ { account, contacts }, ... ] }. Used only for corporate_sponsor, ecosystem_partner, investor. */
const getAccountContactsByRole = async (role) => {
    if (!role) return { success: false, error: 'Role is required' };
    return axiosInstance.get(`/account-contact?role=${encodeURIComponent(role)}`);
};

/** PUT account-contact by contact id. Payload: { account: {...}, contact: {...}, contact_2?: {...} }. Used for updating partner/investor registration. */
const updateAccountContact = async (contactId, payload) => {
    if (!contactId) return { success: false, error: 'Contact ID is required' };
    const dataToSend = { ...payload };
    if (dataToSend.contact) {
        const contact = { ...dataToSend.contact };
        if (contact.email != null) contact.email = String(contact.email).trim().toLowerCase();
        if (contact.secondary_email != null) contact.secondary_email = String(contact.secondary_email).trim().toLowerCase();
        if (contact.username != null) contact.username = String(contact.username).trim().toLowerCase();
        dataToSend.contact = contact;
    }
    if (dataToSend.contact_2) {
        const contact2 = { ...dataToSend.contact_2 };
        if (contact2.email != null) contact2.email = String(contact2.email).trim().toLowerCase();
        if (contact2.username != null) contact2.username = String(contact2.username).trim().toLowerCase();
        dataToSend.contact_2 = contact2;
    }
    return axiosInstance.put(`/account-contact/${contactId}`, dataToSend);
};

const getAllUsers = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/user?${queryParams}` : '/user';
    return axiosInstance.get(url);
}

const getUserById = async (id) => {
    return axiosInstance.get(`/user/${id}`);
}

/** Check if a value (email or username) already exists. Returns { success, data: { exists, field? } }. Skips global loader for better UX. Value is sent as lowercase for consistent checks. */
const checkUserExists = async (value) => {
    if (!value || !String(value).trim()) return { success: true, data: { exists: false } };
    const normalized = String(value).trim().toLowerCase();
    const url = `/user/check?value=${encodeURIComponent(normalized)}`;
    return axiosInstance.get(url, { skipLoader: true });
}

const getUsersByRole = async (role, params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/user/role/${role}?${queryParams}` : `/user/role/${role}`;
    return axiosInstance.get(url);
}

// Donor Transactions — admins list all; other roles scope by logged-in user id.
const getAllTransactions = async ({ role, userId } = {}) => {
    if (role === 'admin') {
        return axiosInstance.get('/transactions');
    }
    const id = userId;
    if (id !== null && id !== undefined && String(id).trim() !== '') {
        return axiosInstance.get(
            `/transactions?user_id=${encodeURIComponent(String(id))}`
        );
    }
    return axiosInstance.get('/transactions');
};

/** Stripe donation checkout — `amount` in USD; uses logged-in user email/id in payload. Returns { success, sessionId?, url }. */
const createDonationCheckoutSession = async ({ customerEmail, amount, user_id }) => {
    return axiosInstance.post('/create-checkout-session', {
        customerEmail: String(customerEmail || '').trim().toLowerCase(),
        amount: Number(amount),
        user_id: typeof user_id === 'number' && !Number.isNaN(user_id) ? user_id : Number(user_id),
    });
};

const getPaymentReceipt = async (transactionId, redirect = false) => {
    if (!transactionId) return { success: false, error: 'Transaction ID is required' };
    const query = redirect ? '?redirect=true' : '';
    return axiosInstance.get(`/payment/receipt/${encodeURIComponent(transactionId)}${query}`);
}

const updateUser = async (id, data) => {
    const dataToSend = { ...data };
    if (dataToSend.username != null) {
        dataToSend.username = String(dataToSend.username).trim().toLowerCase();
    }
    if (dataToSend.email != null) {
        dataToSend.email = String(dataToSend.email).trim().toLowerCase();
    }
    if (dataToSend.contact_email != null) {
        dataToSend.contact_email = String(dataToSend.contact_email).trim().toLowerCase();
    }
    if (dataToSend.secondary_contact_email != null) {
        dataToSend.secondary_contact_email = String(dataToSend.secondary_contact_email).trim().toLowerCase();
    }
    if (dataToSend.secondary_email != null) {
        dataToSend.secondary_email = String(dataToSend.secondary_email).trim().toLowerCase();
    }
    return axiosInstance.put(`/user/${id}`, dataToSend);
}

const uploadUserImage = async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return axiosInstance.post('/user/upload_image', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

// Upload image for idea (uses /users/upload, returns URL for idea_image)
const uploadIdeaImage = async (imageFile) => {
    const formData = new FormData();
    formData.append('image', imageFile);
    return axiosInstance.post('/user/upload', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
}

const deleteUser = async (id) => {
    return axiosInstance.delete(`/user/${id}`);
}

/** Admin: resend mentor invite email. Body matches POST /mentor/resend-invite */
const resendMentorInvite = async (email) => {
    const payload = {
        email: String(email || '').trim().toLowerCase(),
        admin_added: true,
    };
    return axiosInstance.post('/mentor/resend-invite', payload);
};

// Event Management
const createEvent = async (data) => {
    return axiosInstance.post('/event', data);
}

const getAllEvents = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/event?${queryParams}` : '/event';
    return axiosInstance.get(url);
}

const getEventById = async (id) => {
    return axiosInstance.get(`/event/${id}`);
}

const getEventsByRole = async (role) => {
    return axiosInstance.get(`/event/by-role/${role}`);
}

const updateEvent = async (id, data) => {
    return axiosInstance.put(`/event/${id}`, data);
}

const deleteEvent = async (id) => {
    return axiosInstance.delete(`/event/${id}`);
}

// Event Visibility
const updateEventVisibility = async (eventId, data) => {
    return axiosInstance.put(`/event-visibility/event/${eventId}`, data);
}

const getEventVisibilities = async (eventId) => {
    return axiosInstance.get(`/event-visibility/event/${eventId}`);
}

// Email Templates
const getAllEmailTemplates = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/email-template?${queryParams}` : '/email-template';
    return axiosInstance.get(url);
}

const getEmailTemplateById = async (id) => {
    return axiosInstance.get(`/email-template/${id}`);
}

const createEmailTemplate = async (data) => {
    return axiosInstance.post('/email-template', data);
}

const updateEmailTemplate = async (id, data) => {
    return axiosInstance.put(`/email-template/${id}`, data);
}

const deleteEmailTemplate = async (id) => {
    return axiosInstance.delete(`/email-template/${id}`);
}

const sendBulkEmail = async (data) => {
    return axiosInstance.post('/email-template/send-bulk', data);
}

const sendEmail = async (data) => {
    const payload = { ...data };
    if (payload.to != null) {
        payload.to = String(payload.to).trim().toLowerCase();
    }
    if (Array.isArray(payload.cc)) {
        payload.cc = payload.cc.map((email) => String(email).trim().toLowerCase()).filter(Boolean);
    }
    if (Array.isArray(payload.bcc)) {
        payload.bcc = payload.bcc.map((email) => String(email).trim().toLowerCase()).filter(Boolean);
    }
    return axiosInstance.post('/send-email', payload);
}

// Mentor Availability
const createMentorAvailability = async (data) => {
    return axiosInstance.post('/mentor-availability', data);
}

const getAllMentorAvailabilities = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/mentor-availability?${queryParams}` : '/mentor-availability';
    return axiosInstance.get(url);
}

const getMentorAvailabilityById = async (id) => {
    return axiosInstance.get(`/mentor-availability/${id}`);
}

const updateMentorAvailability = async (id, data) => {
    return axiosInstance.put(`/mentor-availability/${id}`, data);
}

const deleteMentorAvailability = async (id) => {
    return axiosInstance.delete(`/mentor-availability/${id}`);
}

const getAvailabilityByUserId = async (userId) => {
    return axiosInstance.get(`/availability/user/${userId}`);
}

// Event Registration
const createEventRegistration = async (data) => {
    return axiosInstance.post('/event-registration', data);
}

const getAllEventRegistrations = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/event-registration?${queryParams}` : '/event-registration';
    return axiosInstance.get(url);
}

const getEventRegistrationById = async (id) => {
    return axiosInstance.get(`/event-registration/${id}`);
}

const updateEventRegistration = async (id, data) => {
    return axiosInstance.put(`/event-registration/${id}`, data);
}

const deleteEventRegistration = async (id) => {
    return axiosInstance.delete(`/event-registration/${id}`);
}

// Task Management
const createTask = async (data) => {
    return axiosInstance.post('/task', data);
}

const getAllTasks = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/task?${queryParams}` : '/task';
    return axiosInstance.get(url);
}

const getTaskById = async (id) => {
    return axiosInstance.get(`/task/${id}`);
}

const updateTask = async (id, data) => {
    return axiosInstance.put(`/task/${id}`, data);
}

const deleteTask = async (id) => {
    return axiosInstance.delete(`/task/${id}`);
}

const getTasksByUserId = async (userId) => {
    return axiosInstance.get(`/task/byuserId/${userId}`);
}

const createUserTask = async (data) => {
    return axiosInstance.post('/task/create-user-task', data);
}

// Idea Management
const createIdea = async (data) => {
    return axiosInstance.post('/idea', data);
}

const getAllIdeas = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/idea?${queryParams}` : '/idea';
    return axiosInstance.get(url);
}

/** All idea–user rows (student, mentor, etc.) for admin ideas list */
const getAllIdeaUsers = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/idea-user?${queryParams}` : '/idea-user';
    return axiosInstance.get(url);
}

const getIdeaById = async (id) => {
    return axiosInstance.get(`/idea/${id}`);
}

// All of an idea's student-chosen mentor preferences (1/2/3), in order, each
// with the mentor's info, decision status and rejection reason.
const getIdeaMentorPreferences = async (id) => {
    return axiosInstance.get(`/idea/${id}/mentor-preferences`);
}

// Notify the admin that no mentor matches an idea's innovation category.
const notifyNoMentorMatch = async (id) => {
    return axiosInstance.post(`/idea/${id}/notify-no-mentor`);
}

const getIdeasByUserId = async (userId) => {
    return axiosInstance.get(`/idea-user/${userId}`);
}

const createIdeaUser = async (data) => {
    return axiosInstance.post('/idea-user', data);
}

/** Invite a co-founder/team member by name + email (sends them a signup link) */
const inviteTeamMember = async (data) => {
    // data: { idea_id, email, first_name, last_name, invited_by }
    return axiosInstance.post('/idea-team-invitation', data);
}

/** Pending (or filtered) team invitations for an idea */
const getTeamInvitations = async (ideaId, status = 'PENDING') => {
    const params = new URLSearchParams({ idea_id: ideaId });
    if (status) params.append('status', status);
    return axiosInstance.get(`/idea-team-invitation?${params.toString()}`);
}

/** Validate a team-invite token and read its details (public, used by the signup page) */
const getTeamInvitationByToken = async (token) => {
    return axiosInstance.get(`/idea-team-invitation/by-token?token=${encodeURIComponent(token)}`);
}

/** Accept a team invitation: completes signup + links as SECONDARY_STUDENT (public) */
const acceptTeamInvitation = async (data) => {
    // data: { token, username, password, first_name, last_name, phone }
    return axiosInstance.post('/idea-team-invitation/accept', data);
}

const updateIdeaUser = async (id, data) => {
    return axiosInstance.patch(`/idea-user/${id}`, data);
}

const createIdeaMentorPreference = async (data) => {
    return axiosInstance.post('/idea-mentor-preference', data);
}

/** Primary mentor invites a secondary (co-)mentor to an active idea */
const inviteSecondaryMentor = async (data) => {
    // data: { idea_id, mentor_id, invited_by }
    return axiosInstance.post('/idea-mentor-preference/invite', data);
}

/** Pending secondary-mentor invitations for a mentor (shown in My Engagements) */
const getMentorInvites = async (mentorId) => {
    return axiosInstance.get(`/idea-mentor-preference/invites?mentor_id=${mentorId}`);
}

/** Accept or decline a secondary-mentor invitation. data: { accept, mentor_id } */
const respondToMentorInvite = async (id, data) => {
    return axiosInstance.patch(`/idea-mentor-preference/${id}/respond`, data);
}

/** Move an INACTIVE idea back to PENDING so it enters mentor review */
const activateIdea = async (id) => {
    return axiosInstance.patch(`/idea/${id}/activate`);
}

const getMentorReviewIdeas = async (mentorId) => {
    return axiosInstance.get(`/idea/get-mentor-review-idea?mentor_id=${mentorId}`);
}

const getMentorIdeas = async (mentorId) => {
    return axiosInstance.get(`/idea/get-mentor-ideas?mentor_id=${mentorId}`);
}

const updateIdea = async (id, data) => {
    return axiosInstance.patch(`/idea/${id}`, data);
}

const updateIdeaDecision = async (ideaId, mentorId, decisionReason, status) => {
    return axiosInstance.patch(`/idea/${ideaId}`, {
        id: ideaId,
        status: status,
        decision_reason: decisionReason,
        decided_by: mentorId
    });
}

const acceptMentorship = async (ideaId, mentorId, decisionReason) => {
    return updateIdeaDecision(ideaId, mentorId, decisionReason, "ACTIVE");
}

// Sequential mentor routing: the currently-assigned mentor declines the idea. The
// backend hands it off to the student's next preferred mentor (or finalizes it as
// REJECTED and notifies the student + admin when none remain).
const rejectMentorship = async (ideaId, mentorId, decisionReason) => {
    return axiosInstance.post(`/idea/${ideaId}/mentor-reject`, {
        mentor_id: mentorId,
        decision_reason: decisionReason
    });
}

// Admin declines on behalf of the currently-assigned mentor (same handoff effect).
const adminRejectOnBehalf = async (ideaId, decisionReason) => {
    return axiosInstance.post(`/idea/${ideaId}/admin-reject-on-behalf`, {
        decision_reason: decisionReason
    });
}

// Admin terminally rejects the idea at any time; stops routing and notifies the student.
const adminRejectIdea = async (ideaId, decisionReason) => {
    return axiosInstance.post(`/idea/${ideaId}/admin-reject`, {
        decision_reason: decisionReason
    });
}

// Admin reassigns the idea to a specific mentor (same or different); that mentor
// becomes the current assignee regardless of preference order.
const reassignIdeaMentor = async (ideaId, mentorId, studentId) => {
    return axiosInstance.post(`/idea/${ideaId}/reassign-mentor`, {
        mentor_id: mentorId,
        student_id: studentId
    });
}

/** GET stages and questions for an idea. The viewer_id controls which mentor
 * reviews are returned: the primary mentor sees all, a secondary mentor sees only
 * their own, and students/admins see only the primary mentor's scores.
 * Response: { success, data: { ideaId, stages: [{ stageId, stageNumber, questions: [{ id, question, reviews }] }] } } */
const getIdeaStages = async (ideaId, viewerId) => {
    const url =
        viewerId != null && viewerId !== ""
            ? `/idea/${ideaId}/stages?viewer_id=${encodeURIComponent(viewerId)}`
            : `/idea/${ideaId}/stages`;
    return axiosInstance.get(url);
};

/** POST mentor answers and scores for an idea (stage-wise). Payload: { mentor_id, answers: [{ stageId, questionId, answer, score? }] } */
const submitMentorStageAnswers = async (ideaId, payload) => {
    return axiosInstance.post(`/idea/${ideaId}/stages`, payload);
};

/** GET all global stage rubric questions (shared across every idea). Each row: { id, stage_number, question } */
const getAllStageQuestions = async () => {
    return axiosInstance.get('/stage-question');
};

/** PUT update a global stage question. Payload: { stage_number?, question? } */
const updateStageQuestion = async (id, payload) => {
    return axiosInstance.put(`/stage-question/${id}`, payload);
};

// FAQ Management
const createFAQ = async (data) => {
    return axiosInstance.post('/faq', data);
}

const getAllFAQs = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/faq?${queryParams}` : '/faq';
    return axiosInstance.get(url);
}

const getFAQById = async (id) => {
    return axiosInstance.get(`/faq/${id}`);
}

const updateFAQ = async (id, data) => {
    return axiosInstance.put(`/faq/${id}`, data);
}

const deleteFAQ = async (id) => {
    return axiosInstance.delete(`/faq/${id}`);
}

// Configuration Management (active_idea, overall_idea)
const createConfiguration = async (data) => {
    return axiosInstance.post('/configuration', data);
}

const getAllConfigurations = async () => {
    return axiosInstance.get('/configuration');
}

const getConfigurationById = async (id) => {
    return axiosInstance.get(`/configuration/${id}`);
}

const updateConfiguration = async (id, data) => {
    return axiosInstance.put(`/configuration/${id}`, data);
}

const deleteConfiguration = async (id) => {
    return axiosInstance.delete(`/configuration/${id}`);
}

// Testimonial Management
const createTestimonial = async (data) => {
    return axiosInstance.post('/testimonial', data);
}

const getAllTestimonials = async (params = {}) => {
    const queryParams = new URLSearchParams(params).toString();
    const url = queryParams ? `/testimonial?${queryParams}` : '/testimonial';
    return axiosInstance.get(url);
}

const getTestimonialById = async (id) => {
    return axiosInstance.get(`/testimonial/${id}`);
}

const updateTestimonial = async (id, data) => {
    return axiosInstance.put(`/testimonial/${id}`, data);
}

const deleteTestimonial = async (id) => {
    return axiosInstance.delete(`/testimonial/${id}`);
}

// Calendar Invite
const createCalendarInvite = async (data) => {
    return axiosInstance.post('/calendar/invite', data);
}

// Admin Master Data
const createAdminRole = async (data) => {
    return axiosInstance.post('/admin/roles', data);
}

const getAdminRoles = async () => {
    return axiosInstance.get('/admin/roles');
}

const createAdminDropdown = async (data) => {
    return axiosInstance.post('/admin/dropdowns', data);
}

const getAdminDropdowns = async (includeInactive = false) => {
    const url = includeInactive ? '/admin/dropdowns?includeInactive=true' : '/admin/dropdowns';
    return axiosInstance.get(url);
}

const updateAdminDropdown = async (key, data) => {
    return axiosInstance.put(`/admin/dropdowns/${encodeURIComponent(key)}`, data);
}

const deleteAdminDropdown = async (key) => {
    return axiosInstance.delete(`/admin/dropdowns/${encodeURIComponent(key)}`);
}

const createAdminDropdownOptions = async (data) => {
    return axiosInstance.post('/admin/dropdown-options', data);
}

const getAdminDropdownOptions = async (dropdownKey, includeInactive = false) => {
    const queryParams = new URLSearchParams();
    if (dropdownKey) queryParams.set('dropdownKey', dropdownKey);
    if (includeInactive) queryParams.set('includeInactive', 'true');
    const url = queryParams.toString()
        ? `/admin/dropdown-options?${queryParams.toString()}`
        : '/admin/dropdown-options';
    return axiosInstance.get(url);
}

const updateAdminDropdownOption = async (optionId, data) => {
    return axiosInstance.put(`/admin/dropdown-options/${optionId}`, data);
}

const deleteAdminDropdownOption = async (optionId) => {
    return axiosInstance.delete(`/admin/dropdown-options/${optionId}`);
}

// Legacy: toggle option status via POST (if backend supports); prefer updateAdminDropdownOption(optionId, { is_active })
const updateAdminDropdownOptionStatus = async (data) => {
    return axiosInstance.post('/admin/dropdown-options', data);
}

const createAdminRoleMap = async (data) => {
    return axiosInstance.post('/admin/role-map', data);
}

const getAdminRoleMap = async ({ roleName, dropdownKey } = {}) => {
    const queryParams = new URLSearchParams();
    if (roleName) queryParams.set('roleName', roleName);
    if (dropdownKey) queryParams.set('dropdownKey', dropdownKey);
    const url = queryParams.toString()
        ? `/admin/role-map?${queryParams.toString()}`
        : '/admin/role-map';
    return axiosInstance.get(url);
}

// Public dropdown APIs (no auth required for options)
const getPublicDropdownOptions = async (field) => {
    const url = field ? `/dropdowns?field=${encodeURIComponent(field)}` : '/dropdowns';
    return axiosInstance.get(url);
}

const getPublicDropdownsAll = async () => {
    return axiosInstance.get('/dropdowns/all');
}

// Gemini Suggestions (without triggering global loading)
const getGeminiSuggestions = async (data, retryCount = 0) => {
    // Use plain axios to avoid interceptors that trigger loading manager
    const accessToken = localStorage.getItem('accessToken');
    const headers = {
        'Content-Type': 'application/json',
    };
    if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }
    
    try {
        const response = await axios.post(
            `${axiosInstance.defaults.baseURL}gemini/suggestions`,
            data,
            { headers }
        );
        
        // Return in the same format as axiosInstance for consistency
        return { success: true, data: response.data };
    } catch (error) {
        // Handle 401 error and refresh token
        if (error.response?.status === 401 && retryCount === 0) {
            try {
                const refreshToken = localStorage.getItem('refreshToken');
                if (refreshToken) {
                    // Try to refresh the token
                    const refreshResponse = await refreshAccessToken(refreshToken);
                    
                    // Check if refresh token API returned 200
                    if (refreshResponse.status === 200 && refreshResponse.data?.success && refreshResponse.data?.data) {
                        // Update tokens in localStorage
                        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = refreshResponse.data.data;
                        localStorage.setItem('accessToken', newAccessToken);
                        localStorage.setItem('refreshToken', newRefreshToken);
                        
                        // Retry the original request with new access token
                        const retryHeaders = {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${newAccessToken}`
                        };
                        
                        const retryResponse = await axios.post(
                            `${axiosInstance.defaults.baseURL}/gemini/suggestions`,
                            data,
                            { headers: retryHeaders }
                        );
                        
                        return { success: true, data: retryResponse.data };
                    } else {
                        // Refresh token API didn't return 200 or expected format, logout
                        logoutUser();
                        return Promise.reject({
                            success: false,
                            error: 'Token refresh failed'
                        });
                    }
                } else {
                    // No refresh token available, logout
                    logoutUser();
                    return Promise.reject({
                        success: false,
                        error: 'No refresh token available'
                    });
                }
            } catch (refreshError) {
                // If refresh fails, logout
                logoutUser();
                return Promise.reject({
                    success: false,
                    error: refreshError.response?.data?.message || 'Token refresh failed'
                });
            }
        }
        
        // For other errors, return in the same format
        return Promise.reject({
            success: false,
            error: error.response?.data?.message || 'An error occurred'
        });
    }
}

// Message Sent (mentor -> student notifications)
const getMessagesByUserId = async (userId) => {
    return axiosInstance.get(`/message-sent/user/${userId}`);
}

const deleteMessageSent = async (id) => {
    return axiosInstance.delete(`/message-sent/${id}`);
}

export {
    loginUser,
    forgetPassword,
    updatePassword,
    updatePasswordWithUserId,
    sendEmailOtp,
    verifyEmailOtp,
    sendPhoneOtp,
    verifyPhoneOtp,
    createUser,
    createAccountContact,
    getAccountContact,
    getAccountContactsByRole,
    updateAccountContact,
    checkUserExists,
    getAllUsers,
    getUserById,
    getUsersByRole,
    getAllTransactions,
    createDonationCheckoutSession,
    getPaymentReceipt,
    updateUser,
    uploadUserImage,
    uploadIdeaImage,
    deleteUser,
    resendMentorInvite,
    createEvent,
    getAllEvents,
    getEventById,
    getEventsByRole,
    updateEvent,
    deleteEvent,
    updateEventVisibility,
    getEventVisibilities,
    getAllEmailTemplates,
    getEmailTemplateById,
    createEmailTemplate,
    updateEmailTemplate,
    deleteEmailTemplate,
    sendBulkEmail,
    sendEmail,
    createMentorAvailability,
    getAllMentorAvailabilities,
    getMentorAvailabilityById,
    updateMentorAvailability,
    deleteMentorAvailability,
    getAvailabilityByUserId,
    createEventRegistration,
    getAllEventRegistrations,
    getEventRegistrationById,
    updateEventRegistration,
    deleteEventRegistration,
    createTask,
    getAllTasks,
    getTaskById,
    getTasksByUserId,
    createUserTask,
    updateTask,
    deleteTask,
    createIdea,
    getAllIdeas,
    getAllIdeaUsers,
    getIdeaById,
    getIdeaMentorPreferences,
    notifyNoMentorMatch,
    updateIdea,
    getIdeasByUserId,
    createIdeaUser,
    updateIdeaUser,
    inviteTeamMember,
    getTeamInvitations,
    getTeamInvitationByToken,
    acceptTeamInvitation,
    createIdeaMentorPreference,
    inviteSecondaryMentor,
    getMentorInvites,
    respondToMentorInvite,
    activateIdea,
    getMentorReviewIdeas,
    getMentorIdeas,
    acceptMentorship,
    rejectMentorship,
    adminRejectOnBehalf,
    adminRejectIdea,
    reassignIdeaMentor,
    getIdeaStages,
    submitMentorStageAnswers,
    getAllStageQuestions,
    updateStageQuestion,
    createFAQ,
    getAllFAQs,
    getFAQById,
    updateFAQ,
    deleteFAQ,
    createConfiguration,
    getAllConfigurations,
    getConfigurationById,
    updateConfiguration,
    deleteConfiguration,
    createTestimonial,
    getAllTestimonials,
    getTestimonialById,
    updateTestimonial,
    deleteTestimonial,
    getGeminiSuggestions,
    createCalendarInvite,
    createAdminRole,
    getAdminRoles,
    createAdminDropdown,
    getAdminDropdowns,
    updateAdminDropdown,
    deleteAdminDropdown,
    createAdminDropdownOptions,
    getAdminDropdownOptions,
    updateAdminDropdownOption,
    deleteAdminDropdownOption,
    updateAdminDropdownOptionStatus,
    createAdminRoleMap,
    getAdminRoleMap,
    getPublicDropdownOptions,
    getPublicDropdownsAll,
    getMessagesByUserId,
    deleteMessageSent
};
